import { NavLink, Navigate, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { LogOut, LayoutDashboard, CalendarCheck, Mail, Vote, FileText, Activity, Send, Sparkles, Award, Gift } from "lucide-react";

const tabs = [
  { to: "/admin", end: true, label: "Overview", icon: LayoutDashboard, testid: "admin-tab-overview" },
  { to: "/admin/bookings", label: "Bookings", icon: CalendarCheck, testid: "admin-tab-bookings" },
  { to: "/admin/contacts", label: "Messages", icon: Mail, testid: "admin-tab-contacts" },
  { to: "/admin/loyalty", label: "Loyalty", icon: Award, testid: "admin-tab-loyalty" },
  { to: "/admin/gift-cards", label: "Gift Cards", icon: Gift, testid: "admin-tab-gift-cards" },
  { to: "/admin/poll", label: "Facials Poll", icon: Vote, testid: "admin-tab-poll" },
  { to: "/admin/makeup-waitlist", label: "Makeup Waitlist", icon: Sparkles, testid: "admin-tab-makeup-waitlist" },
  { to: "/admin/blog", label: "Blog", icon: FileText, testid: "admin-tab-blog" },
  { to: "/admin/digest", label: "Digest", icon: Send, testid: "admin-tab-digest" },
  { to: "/admin/analytics", label: "Analytics", icon: Activity, testid: "admin-tab-analytics" },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  if (loading) {
    return (
      <main className="min-h-screen bg-[#0F1F4E] text-[#F8F1D8] flex items-center justify-center">
        <div className="font-serif text-2xl">Loading…</div>
      </main>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) {
    return (
      <main className="min-h-screen bg-[#0F1F4E] text-[#F8F1D8] flex items-center justify-center px-6">
        <div className="text-center" data-testid="not-authorized">
          <h1 className="font-serif text-4xl mb-3">Not authorized</h1>
          <p className="text-[#D6D6BD] mb-6">Signed in as {user.email}.</p>
          <button
            onClick={async () => { await logout(); navigate("/login", { replace: true }); }}
            className="px-6 py-3 rounded-full bg-[#EAD584] text-[#0F1F4E] text-sm uppercase tracking-wider"
          >
            Sign out
          </button>
        </div>
      </main>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF6E8] text-[#0F1F4E]" data-testid="admin-shell">
      <header className="bg-[#0F1F4E] text-[#F8F1D8]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <div>
            <div className="overline-gold mb-1">Beauty &amp; Balance · Studio Admin</div>
            <h1 className="font-serif text-2xl tracking-tight">{user.name || user.email}</h1>
          </div>
          <button
            type="button"
            onClick={async () => { await logout(); navigate("/login", { replace: true }); }}
            data-testid="admin-logout-btn"
            className="inline-flex items-center gap-2 text-sm uppercase tracking-wider text-[#EAD584] hover:text-[#F0DC8A]"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
        <nav className="bg-[#142a66] border-t border-[#EAD584]/20">
          <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-wrap gap-1 overflow-x-auto">
            {tabs.map(({ to, end, label, icon: Icon, testid }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                data-testid={testid}
                className={({ isActive }) =>
                  `inline-flex items-center gap-2 px-4 py-3 text-xs uppercase tracking-wider font-light border-b-2 transition-colors ${
                    isActive
                      ? "border-[#EAD584] text-[#EAD584]"
                      : "border-transparent text-[#D6D6BD] hover:text-[#F0DC8A]"
                  }`
                }
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </NavLink>
            ))}
          </div>
        </nav>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <Outlet />
      </main>
    </div>
  );
}
